import type { ReactNode } from "react";
import { BluebirdLayout } from "@/components/bluebird/BluebirdLayout";
import { BreadcrumbJsonLd } from "@/components/shared/json-ld";
import { SlimFooter, SubHero, SubMasthead } from "@/components/bluebird/Chrome";
import { A, Button, H2 } from "@/components/bluebird/primitives";
import { color, font, maxWidth, radius, space, type } from "@/lib/design-tokens";
import { savings } from "@/lib/copy/savings";

/**
 * The merged money page.
 *
 * `/financing`, `/trade-in`, `/tax-voucher` and `/rebate` 301 here, each to its
 * own `#anchor` — see config/routes.rb and test/integration/redirects_test.rb.
 * The anchors are load-bearing: they are what makes those redirects land a
 * visitor on the thing they clicked instead of the top of a long page.
 */
function Savings() {
  return (
    <>
      {/* Structured data only — renders no visible markup, so it cannot move a
          pixel in script/design_diff.mjs. Kept from the previous design because
          breadcrumbs are what let a search result show the path to the page. */}
      <BreadcrumbJsonLd items={[{ name: "Savings", href: "/savings" }]} />

      <SubMasthead
        current="/savings"
        photo="hero-savings"
        photoAlt="A family riding an electric cargo bike"
      >
        <SubHero
          line1={savings.hero.line1}
          line2={savings.hero.line2}
          intro={savings.hero.intro}
          introMaxWidth="620px"
        />
      </SubMasthead>

      {/* The <main> landmark. A screen-reader user's "skip to content" jump
          lands here, and without it the only landmarks on the page are a banner
          and a footer with everything else unlabelled. <main> is display:block
          with no margin, so it wraps the same boxes at the same positions —
          verified by script/design_diff.mjs, which still reports 0%. */}
      <main>

      {savings.sections.map((section, i) => {
        const onBlue = i % 2 === 1;
        return (
          <section
            key={section.id}
            id={section.id}
            data-section={section.id}
            style={{
              background: onBlue ? color.blue : color.white,
              color: onBlue ? color.white : color.ink,
              // Anchored sections need to clear the top of the viewport when a
              // 301 lands mid-page.
              scrollMarginTop: "24px",
            }}
          >
            <div
              style={{
                maxWidth,
                margin: "0 auto",
                padding: `${space.sectionY} ${space.gutter}`,
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit,minmax(min(300px,100%),1fr))",
                gap: "clamp(32px,5vw,72px)",
                alignItems: "start",
              }}
            >
              <div>
                <div
                  style={{
                    fontFamily: font.mono,
                    fontSize: "12px",
                    letterSpacing: ".14em",
                    textTransform: "uppercase",
                    color: onBlue ? color.gold : color.slateLight,
                  }}
                >
                  {section.eyebrow}
                </div>
                <H2 data-reveal style={{ marginTop: "14px", maxWidth: "24ch" }}>
                  {section.heading}
                </H2>
                {section.body.map((para, n) => (
                  <p
                    key={n}
                    style={{
                      margin: n === 0 ? "20px 0 0" : "16px 0 0",
                      fontSize: "16.5px",
                      lineHeight: 1.6,
                      color: onBlue ? color.onBlue : color.slate,
                      maxWidth: "56ch",
                    }}
                  >
                    {para}
                  </p>
                ))}
                {/* Official partner marks, nominative use — provenance and the
                    published-terms check live in public/images/partners/PROVENANCE.md. */}
                {(section.id === "financing" || section.id === "trade-in") && (
                  <div
                    data-partner-mark={section.id}
                    style={{ marginTop: "22px", display: "flex", alignItems: "center", gap: "12px" }}
                  >
                    <img
                      src={section.id === "financing" ? "/images/partners/cecu-logo.png" : "/images/partners/upway-logo.png"}
                      alt={section.id === "financing" ? "Clean Energy Credit Union logo" : "Upway logo"}
                      style={{ height: section.id === "financing" ? "34px" : "36px", width: "auto", display: "block" }}
                    />
                    <span style={{ fontSize: "13px", color: onBlue ? color.onBlue : color.slate }}>
                      {section.id === "financing" ? "Clean Energy Credit Union" : "Upway"}
                    </span>
                  </div>
                )}
                <div style={{ marginTop: "24px", display: "flex", gap: "20px", flexWrap: "wrap", alignItems: "center" }}>
                  <Button
                    href={section.linkHref}
                    variant={onBlue ? "goldSoft" : "blue"}
                    style={{ fontSize: "16px", padding: "15px 26px" }}
                    {...(section.linkHref.startsWith("http")
                      ? { target: "_blank", rel: "noopener noreferrer" }
                      : {})}
                  >
                    {section.linkLabel}
                  </Button>
                  {"secondaryLinkLabel" in section && section.secondaryLinkLabel && (
                    <A
                      href={section.secondaryLinkHref as string}
                      className="bb-arrow-link bb-arrow-link-gold"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: onBlue ? color.gold : color.blue, fontWeight: 700, fontSize: "16px" }}
                    >
                      {section.secondaryLinkLabel} →
                    </A>
                  )}
                </div>
              </div>

              <div
                data-reveal="rise"
                style={{
                  justifySelf: "start",
                  background: onBlue ? "rgba(255,255,255,.08)" : color.gold,
                  borderRadius: radius.panel,
                  padding: "clamp(24px,3vw,36px)",
                  minWidth: "220px",
                }}
              >
                <div
                  style={{
                    fontWeight: 800,
                    // `type.figure` — the comp's own token for a big dollar
                    // figure, which is exactly what this is. It previously
                    // carried a hand-written clamp(34px,4.5vw,52px) that
                    // resolved to 52px/34px: two sizes on no step of the comp's
                    // type scale. script/token_conformance.mjs found it.
                    fontSize: type.figure,
                    lineHeight: 0.95,
                    letterSpacing: "-.02em",
                    color: onBlue ? color.gold : color.blue,
                  }}
                >
                  {section.figure}
                </div>
                <div
                  style={{
                    marginTop: "12px",
                    fontSize: "15px",
                    lineHeight: 1.45,
                    color: onBlue ? color.onBlue : color.onGold,
                    maxWidth: "22ch",
                  }}
                >
                  {section.figureNote}
                </div>
              </div>
            </div>
          </section>
        );
      })}

      <section
        data-section="counter"
        style={{ maxWidth, margin: "0 auto", padding: `${space.sectionY} ${space.gutter}` }}
      >
        <H2 data-reveal style={{ maxWidth: "20ch" }}>
          {savings.counterHeading}
        </H2>
        <p style={{ margin: "18px 0 0", fontSize: "16.5px", lineHeight: 1.6, color: color.slate, maxWidth: "56ch" }}>
          {savings.counterBody}
        </p>
        <div style={{ marginTop: "28px" }}>
          <Button
            href="/book"
            variant="red"
            style={{ fontSize: "16px", padding: "15px 26px" }}
          >
            {savings.counterCta}
          </Button>
        </div>
      </section>

      </main>

      <SlimFooter borderTop />
    </>
  );
}

Savings.layout = (page: ReactNode) => <BluebirdLayout>{page}</BluebirdLayout>;

export default Savings;
